import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

type AddressUncommittedContextValue = {
  /** Reports whether the address input identified by `id` has an unapplied search query. */
  report: (id: string, isUncommitted: boolean) => void;
};

const AddressUncommittedContext =
  createContext<AddressUncommittedContextValue | null>(null);

/**
 * Tracks, for a single form, which address inputs have a typed-but-unapplied
 * search query. One tracker handles
 * any number of address inputs — including the multi-address editors — without
 * the form having to thread per-row state through its field components.
 *
 * The submit owner reads `hasUncommittedAddress` to block submission and wraps
 * its form in `AddressUncommittedProvider value={contextValue}`. Each
 * `AddressInput` underneath reports its own slot via `useReportAddressUncommitted`.
 */
export const useAddressUncommittedTracker = () => {
  const [uncommittedIds, setUncommittedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const report = useCallback((id: string, isUncommitted: boolean) => {
    setUncommittedIds((previous) => {
      if (isUncommitted === previous.has(id)) {
        return previous;
      }
      const next = new Set(previous);
      if (isUncommitted) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const uncommittedContextValue = useMemo(() => ({ report }), [report]);

  return {
    hasUncommittedAddress: uncommittedIds.size > 0,
    uncommittedContextValue,
  };
};

type ProviderProps = {
  value: AddressUncommittedContextValue;
  children: ReactNode;
};

export const AddressUncommittedProvider = ({
  value,
  children,
}: ProviderProps) => (
  <AddressUncommittedContext.Provider value={value}>
    {children}
  </AddressUncommittedContext.Provider>
);

/**
 * Used by `AddressInput` to report whether its search query is unapplied.
 * Returns a stable reporter bound to this input's own slot. No-ops gracefully
 * when rendered outside a provider (the input still shows its inline error;
 * only the submit-blocking is skipped).
 */
export const useReportAddressUncommitted = () => {
  const context = useContext(AddressUncommittedContext);
  const id = useId();
  const report = context?.report;

  // Release this slot when the input unmounts (e.g. a removed address row), so
  // a stale uncommitted flag can't block the form after its input is gone.
  useEffect(() => () => report?.(id, false), [report, id]);

  return useCallback(
    (isUncommitted: boolean) => report?.(id, isUncommitted),
    [report, id],
  );
};
